// ─────────────────────────────────────────────
//  Facility Settings roster panel — Anesthesia Staff, plus the roster widgets
//  every roster-shaped panel shares. Extracted from setupPage.facility.js for
//  file-size compliance.
//
//  StaffPanel     — shared across facilities; {name, phone, role,
//                   primaryFacilityId, employmentType} grouped into four boxes
//                   by role, locums vs permanent colour-coded.
//
//  ⚠️ THE SURGEONS PANEL LIVES IN setupPage.surgeons.js NOW, and it is not the
//  same panel that used to be here. The old one wrote `surgeonList[]`, an array
//  FIELD on procedure_refs/{facilityId}; the surgeon roster is a real
//  collection now (`surgeons`, one document each) so it can carry an email and
//  a per-record `listed` opt-out and be gated separately from the scheduling
//  data. See that file's header for which list is authoritative for what.
//
//  Hover interaction (RosterChip): hovering a name reveals a tooltip with the
//  phone number and — only where editing is allowed — an Edit button. The
//  tooltip lingers (close delay + stays open while the cursor is over it) so
//  there is time to reach the button. Editing happens ONLY in a modal opened
//  from that button; the inline add form is for new entries. RosterChip,
//  ModalButtons and Field are exported so other screens — the surgeons panel
//  included — show the SAME interaction instead of inventing a second one.
// ─────────────────────────────────────────────
import React, { useEffect, useId, useRef, useState } from "react";
import {
  listenToStaff, addStaff, updateStaff, removeStaff,
} from "../firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { KNOWN_FACILITIES, facilityLabel } from "../domain/facilities";
import Modal from "../components/Modal";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

// Locums get a distinct amber treatment; permanent staff the house blue.
const EMP_COLOR = { locums: color.accent.closing, permanent: color.accent.primary };

// How long the tooltip stays after the cursor leaves, so it can be reached.
const TOOLTIP_CLOSE_MS = 600;

// ── Shared: a name "chip" whose hover tooltip shows phone (+ Edit if allowed) ──
export function RosterChip({ name, phone, accent = color.accent.primary, canEdit = false, onEdit, onRemove, busy }) {
  const [open, setOpen] = useState(false);
  // One id per chip: a roster renders dozens, and a shared id would point every
  // trigger at the first tooltip on the page.
  const tooltipId = `${useId()}-roster-tip`;
  const timer = useRef(null);
  const show = () => { clearTimeout(timer.current); setOpen(true); };
  const hideSoon = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), TOOLTIP_CLOSE_MS); };
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <span
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={show}
      onMouseLeave={hideSoon}
    >
      {/* ⚠️ A BUTTON, NOT A tabIndex'd SPAN. This chip reveals a tooltip holding
          the phone number and the edit/remove actions, so it has to be
          focusable — and `tabIndex={0}` on a <span> makes something focusable
          that announces itself as nothing, which is how a keyboard user lands
          on an element a screen reader has no name or role for. A real button
          is focusable, operable and named for free, and aria-describedby ties
          the revealed tooltip to it so its content is announced rather than
          merely displayed. */}
      <button
        type="button"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "5px 11px", borderRadius: 20,
          background: `${accent}14`, border: `1px solid ${accent}55`,
          color: accent, fontSize: 12, fontWeight: 600, opacity: busy ? 0.6 : 1,
          font: "inherit", fontFamily: "inherit", cursor: "default",
        }}
        aria-describedby={open ? tooltipId : undefined}
        onFocus={show}
        onBlur={hideSoon}
        onClick={() => (open ? hideSoon() : show())}
      >
        {name}
      </button>
      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          onMouseEnter={show}
          onMouseLeave={hideSoon}
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20,
            display: "flex", alignItems: "center", gap: 10, whiteSpace: "nowrap",
            background: color.surface.card, border: `1px solid ${color.border.default}`,
            borderRadius: 8, padding: "7px 10px", boxShadow: "0 4px 14px rgba(0,0,0,.12)",
          }}
        >
          <span style={{ fontSize: 12, color: color.text.secondary }}>
            📞 {phone ? phone : "No phone"}
          </span>
          {canEdit && (
            <>
              <button style={{ ...s.ghostBtn, color: color.accent.primary, padding: "0 4px" }}
                onClick={onEdit} disabled={busy}>Edit</button>
              <button style={{ ...s.ghostBtn, color: color.accent.error, padding: "0 4px" }}
                onClick={onRemove} disabled={busy} aria-label={`Remove ${name}`}>×</button>
            </>
          )}
        </span>
      )}
    </span>
  );
}

// Modal footer Save/Cancel — shared by every roster edit modal.
export function ModalButtons({ onSave, onCancel, busy, saveLabel = "Save" }) {
  return (
    <>
      <button style={s.outlineBtn} onClick={onCancel} disabled={busy}>Cancel</button>
      <button style={s.primaryBtn} onClick={onSave} disabled={busy}>{saveLabel}</button>
    </>
  );
}

// A labelled field for the edit modals.
//
// ⚠️ THE LABEL HAS TO BE TIED TO THE CONTROL, AND THE LINTER CANNOT SEE THAT IT
// WASN'T. `label-has-associated-control` only fires where a <label> and its
// input sit in the same JSX; here `children` is opaque, so 35 call sites across
// 7 files rendered a label bound to nothing and no rule complained. To a screen
// reader those fields are unnamed: "edit text" with no idea which one.
//
// ⚠️ THE ID IS ONLY INJECTED INTO A REAL DOM CONTROL. Cloning `id` onto a custom
// component that does not forward it would leave htmlFor pointing at an element
// that never exists — announced as a missing target, which is worse than the
// silence it replaced. So: string element types only (input/select/textarea),
// and a child that already has an id keeps it.
export function Field({ label, children }) {
  const auto = useId();
  const isDomControl =
    React.isValidElement(children) && typeof children.type === "string";
  const id = isDomControl ? children.props.id || auto : undefined;
  const control = isDomControl && !children.props.id
    ? React.cloneElement(children, { id })
    : children;
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={s.label} htmlFor={id}>{label}</label>
      {control}
    </div>
  );
}

// ── ANESTHESIA STAFF (shared across facilities) ──
const STAFF_ROLES = [
  { value: "doctor",   label: "Doctor",   box: "Doctors" },
  { value: "crna",     label: "CRNA",     box: "CRNAs" },
  { value: "aa",       label: "AA",       box: "AAs" },
  { value: "resident", label: "Resident", box: "Residents" },
];

export function StaffPanel() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const blank = { name: "", phone: "", role: "crna", shortCode: "", primaryFacilityId: KNOWN_FACILITIES[0]?.id || "default", employmentType: "permanent" };
  const [form, setForm] = useState(blank);     // add form
  const [editId, setEditId] = useState(null);  // modal: staff id being edited
  const [draft, setDraft] = useState(blank);   // modal working copy

  useEffect(() => {
    const unsub = listenToStaff(
      (snap) => setStaff(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => { console.error("Load staff failed:", err); toast.error("Could not load staff."); }
    );
    return unsub;
  }, [toast]);

  // Add (inline form) — new entries only.
  const add = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Enter a staff name."); return; }
    setBusy(true);
    try {
      await addStaff({ ...form, name, phone: form.phone.trim() });
      toast.success(`"${name}" added.`);
      setForm(blank);
    } catch (err) {
      console.error("Add staff failed:", err);
      toast.error("Could not add staff. Admin rights are required.");
    } finally { setBusy(false); }
  };

  // Edit (modal) — open / save.
  const openEdit = (m) => {
    setEditId(m.id);
    setDraft({
      name: m.name || "", phone: m.phone || "", role: m.role || "crna",
      shortCode: m.shortCode || "",
      primaryFacilityId: m.primaryFacilityId || KNOWN_FACILITIES[0]?.id || "default",
      employmentType: m.employmentType || "permanent",
    });
  };
  const closeEdit = () => setEditId(null);
  const saveEdit = async () => {
    const name = draft.name.trim();
    if (!name) { toast.error("Enter a staff name."); return; }
    setBusy(true);
    try {
      await updateStaff(editId, { ...draft, name, phone: draft.phone.trim(), shortCode: (draft.shortCode || "").trim().toUpperCase() });
      toast.success(`"${name}" updated.`);
      closeEdit();
    } catch (err) {
      console.error("Save staff failed:", err);
      toast.error("Could not save staff. Admin rights are required.");
    } finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove "${m.name}" from the staff pool?`)) return;
    setBusy(true);
    try { await removeStaff(m.id); toast.info(`"${m.name}" removed.`); }
    catch (err) { console.error("Remove staff failed:", err); toast.error("Could not remove staff."); }
    finally { setBusy(false); }
  };

  // ⚠️ THE "IMPORT EXISTING PROVIDERS" ACTION WAS REMOVED ON 2026-08-19, AND
  // MUST NOT COME BACK IN THIS FORM. It called seedStaff(), which read two
  // hardcoded arrays — src/firebase/seedDoctors.js and seedProviders.js —
  // holding 194 real clinicians' names AND PERSONAL MOBILE NUMBERS.
  //
  // Those arrays were `import`ed at module scope, so the bundler put every one
  // of those numbers into the PRODUCTION JavaScript bundle. `SHOW_SEED_TOOLS`
  // gated the BUTTON, never the DATA: anyone who loaded the site, signed in or
  // not, could read the whole roster's phone numbers out of the served JS. The
  // file comments said "non-production builds only", which was true of the
  // button and false of the people.
  //
  // There is deliberately NO replacement button. Staff are added one at a time
  // in the panel below, which is how a hire actually arrives. Bulk CONTACT
  // details (phone, email, listed) go through scripts/seedDirectoryContacts.mjs
  // — server side, dry-run by default, reading a JSON file the operator
  // supplies and which is never committed. That script pointedly refuses to
  // CREATE staff: a clinical roster is not something a script should invent
  // members of, and the old button did exactly that from a list nobody had
  // reviewed in months.

  const boxes = STAFF_ROLES.map((r) => ({
    ...r,
    members: staff
      .filter((m) => (m.role || "crna") === r.value)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
  }));

  return (
    <div style={s.panel}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ ...s.panelTitle, marginBottom: 0 }}>👥 Anesthesia Staff</div>
      </div>
      <p style={{ fontSize: 12, color: color.text.muted, marginTop: 0, marginBottom: 14 }}>
        Shared across all facilities. Hover a name for phone + edit.
        <span style={{ color: EMP_COLOR.permanent, marginLeft: 8 }}>● Permanent</span>
        <span style={{ color: EMP_COLOR.locums, marginLeft: 8 }}>● Locums</span>
      </p>

      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 160px", minWidth: 140 }}>
            <label style={s.label} htmlFor="roster-add-name">Name</label>
            <input id="roster-add-name" style={s.input} placeholder="Provider name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }} disabled={busy} />
          </div>
          <div style={{ width: 140 }}>
            <label style={s.label} htmlFor="roster-add-phone">Phone</label>
            <input id="roster-add-phone" style={s.input} placeholder="(813) 555-0123" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }} disabled={busy} />
          </div>
          <div style={{ width: 120 }}>
            <label style={s.label} htmlFor="roster-add-role">Role</label>
            <select id="roster-add-role" style={s.select} value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })} disabled={busy}>
              {STAFF_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div style={{ width: 180 }}>
            <label style={s.label} htmlFor="roster-add-facility">Primary facility</label>
            <select id="roster-add-facility" style={s.select} value={form.primaryFacilityId}
              onChange={(e) => setForm({ ...form, primaryFacilityId: e.target.value })} disabled={busy}>
              {KNOWN_FACILITIES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div style={{ width: 140 }}>
            <label style={s.label} htmlFor="roster-add-employment">Employment</label>
            <select id="roster-add-employment" style={s.select} value={form.employmentType}
              onChange={(e) => setForm({ ...form, employmentType: e.target.value })} disabled={busy}>
              <option value="permanent">Permanent</option>
              <option value="locums">Locums</option>
            </select>
          </div>
          <button style={s.primaryBtn} onClick={add} disabled={busy || !form.name.trim()}>＋ Add Staff</button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
        {boxes.map((b) => (
          <div key={b.value} style={{ background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: color.text.muted, marginBottom: 10 }}>
              {b.box} · {b.members.length}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {b.members.map((m) => (
                <RosterChip key={m.id} name={m.name}
                  phone={`${m.phone || "No phone"} · ${facilityLabel(m.primaryFacilityId)}`}
                  accent={EMP_COLOR[m.employmentType] || EMP_COLOR.permanent}
                  canEdit={isAdmin} busy={busy}
                  onEdit={() => openEdit(m)} onRemove={() => remove(m)} />
              ))}
              {b.members.length === 0 && <span style={{ fontSize: 12, color: color.text.muted }}>None</span>}
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={editId != null} onClose={closeEdit} title="Edit Staff"
        footer={<ModalButtons onSave={saveEdit} onCancel={closeEdit} busy={busy} />}>
        <Field label="Name">
          <input style={s.input} value={draft.name} autoFocus
            onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        <Field label="Phone">
          <input style={s.input} type="tel" value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
        </Field>
        <Field label="Role">
          <select style={s.select} value={draft.role}
            onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
            {STAFF_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        {draft.role === "doctor" && (
          <Field label="Doctor code">
            <input
              style={{ ...s.input, textTransform: "uppercase" }}
              value={draft.shortCode}
              maxLength={5}
              placeholder="e.g. MAR"
              aria-label="Doctor code (short code)"
              onChange={(e) => setDraft({ ...draft, shortCode: e.target.value.toUpperCase() })}
            />
          </Field>
        )}
        <Field label="Primary facility">
          <select style={s.select} value={draft.primaryFacilityId}
            onChange={(e) => setDraft({ ...draft, primaryFacilityId: e.target.value })}>
            {KNOWN_FACILITIES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </Field>
        <Field label="Employment">
          <select style={s.select} value={draft.employmentType}
            onChange={(e) => setDraft({ ...draft, employmentType: e.target.value })}>
            <option value="permanent">Permanent</option>
            <option value="locums">Locums</option>
          </select>
        </Field>
      </Modal>
    </div>
  );
}
